import * as Yup from "yup";
import { useState } from "react";
import { useFormik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { ACCEPT_INVITATION } from "../../GraphQL/settings/mutations";
import { EToastPosition, EToastColorStatus, IToastData } from "../../interfaces";
import { ButtonRightCorner, PageContainer, ErrorWrapper } from "../../sharedStyles";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Notification from "../Notification";

const formInvitationSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  phoneNumber: Yup.string().required("Phone is required"),
  password: Yup.string()
    .required("Password is required")
    .min(8, 'Password must have a minimum of 8 characters')
    .matches(/\d+/, "Password must contain at least one number")
    .matches(/[a-z]+/, "Password must contain lowercase character (a-z)")
    .matches(/[A-Z]+/, "Password must contain uppercase character (A-Z)")
    .matches(/[@$/!%*<>+#?&~()^:{}]+/, "Password must contain a special character like : @$/!%*<>+#?&~()^:{}"),
  confirmPassword: Yup.string()
    .required('Please retype your password')
    .oneOf([Yup.ref('password')], 'Passwords does not match'),
  token: Yup.string(),
});

const UserInvitation = () => {
  const navigate = useNavigate();
  const token = new URLSearchParams(useLocation().search).get("token") ?? "";
  const [toastData, setToastData] = useState<IToastData>({
    show: false,
    color: EToastColorStatus.SUCCESS,
    position: EToastPosition.BOTTOM_END,
    message: "",
  });

  const [acceptUser] = useMutation(ACCEPT_INVITATION, {
    onCompleted(data) {
      if (data?.acceptInvitation?.ok) {
        setToastData({
          show: true,
          color: EToastColorStatus.SUCCESS,
          position: EToastPosition.BOTTOM_END,
          message: 'Congratulations, you have successfully registered!',
        });
        setTimeout(() => navigate('/', { replace: true }), 2000);
      } else {
        setToastData({
          show: true,
          color: EToastColorStatus.FAILED,
          position: EToastPosition.BOTTOM_END,
          message: 'Something went wrong!',
        });
      }
    },
    onError(error: any) {
      const errorMessage = error?.networkError?.result?.errors[0]?.message;
      setToastData({
        show: true,
        color: EToastColorStatus.FAILED,
        position: EToastPosition.BOTTOM_END,
        message: errorMessage ?? 'Something went wrong!',
      });
    },
  });

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      password: '',
      confirmPassword: '',
      token: token,
    },
    validationSchema: formInvitationSchema,
    onSubmit: (values) => {
      acceptUser({variables: {
        token: values?.token,
        firstName: values?.firstName,
        lastName: values?.lastName,
        phoneNumber: values?.phoneNumber,
        password: values?.password,
      }});
    },
    enableReinitialize: true,
  });

  const { errors, touched } = formik;

  return (
    <PageContainer className="d-flex align-items-center justify-content-center vh-100">
      <Card style={{ width: '36rem', minHeight: '22rem'}}>
        <CardContent className="pt-6">
          <form onSubmit={formik.handleSubmit}>
            <div className="mb-4">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                name="firstName"
                type="text"
                placeholder="Enter your first name"
                onChange={formik.handleChange}
                value={formik?.values?.firstName}
              />
              <ErrorWrapper>{errors?.firstName && touched?.firstName ? errors?.firstName : ''}</ErrorWrapper>
            </div>
            <div className="mb-4">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                name="lastName"
                type="text"
                placeholder="Enter your last name"
                onChange={formik.handleChange}
                value={formik?.values?.lastName}
              />
              <ErrorWrapper>{errors?.lastName && touched?.lastName ? errors?.lastName : ''}</ErrorWrapper>
            </div>
            <div className="mb-4">
              <Label htmlFor="phoneNumber">Phone Number</Label>
              <Input
                id="phoneNumber"
                name="phoneNumber"
                type="text"
                placeholder="Enter your phone number"
                onChange={formik.handleChange}
                value={formik?.values?.phoneNumber}
              />
              <ErrorWrapper>{errors?.phoneNumber && touched?.phoneNumber ? errors?.phoneNumber : ''}</ErrorWrapper>
            </div>
            <div className="mb-4">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="Enter your password"
                onChange={formik.handleChange}
                value={formik?.values?.password}
              />
              <ErrorWrapper>{errors?.password && touched?.password ? errors?.password : ''}</ErrorWrapper>
            </div>
            <div className="mb-4">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="Enter your confirm password"
                onChange={formik.handleChange}
                value={formik?.values?.confirmPassword}
              />
              <ErrorWrapper>{errors?.confirmPassword && touched?.confirmPassword ? errors?.confirmPassword : ''}</ErrorWrapper>
            </div>
          </form>
        </CardContent>
        <CardFooter>
          <ButtonRightCorner>
            <Button
              onClick={formik.submitForm}
            >
              Save
            </Button>
          </ButtonRightCorner>
        </CardFooter>
      </Card>
      <Notification
        delay={3000}
        toastData={toastData}
        onClose={() => setToastData({ ...toastData, show: false })}
      />
    </PageContainer>
  )
}

export default UserInvitation;
